"use client";

import { Motion, useAnimationSequence } from "@ui/hooks/useAnimationSequence";
import {
  animate,
  AnimatePresence,
  type MotionValue,
  useDragControls,
  useMotionValue,
  useTransform,
} from "motion/react";
import Image, { type StaticImageData } from "next/image";
import { useEffect, useRef, useState } from "react";
import { pxToRem } from "@ui/lib/utils";
import { clsx } from "clsx";
import * as motion from "motion/react-m";
import { useTranslations } from "next-intl";
import type { FlowsDemoBlock } from "@mw/shared/payload-types";
import { TabList } from "@ui/components/core/TabList";
import { type Key, Tabs } from "react-aria-components";
import { TabPanel } from "react-aria-components";
import { getLinkProps } from "@utilities/payload";

import imgBoatReference from "./assets/yacht-promo/reference.png";
import imgBoatTopDown from "./assets/yacht-promo/top-down.png";
import imgBoatCloseUp from "./assets/yacht-promo/close-up.png";

import imgPerfumeBottle from "./assets/perfume-ad/bottle.png";
import imgPerfumeModel from "./assets/perfume-ad/model.png";
import imgPerfumeComp from "./assets/perfume-ad/comp.png";
import imgPerfumeBalcony from "./assets/perfume-ad/balcony.png";
import imgPerfumeVideo from "./assets/perfume-ad/video.jpg";

import imgRunner from "./assets/running-track/runner.png";

type Size = { raw: number; css: string };

const STANDARD_WIDTH = "var(--standard-width)";
const GAP = "var(--gap)";
const AUDIO_WIDTH = `min(16.25rem, ${STANDARD_WIDTH})`;
const GAP_3_COL = 148;
const GAP_4_COL = 124;

const CONTAINER_WIDTH = 1176 + 40 * 2;
const SIZES = {
  3: {
    default: {
      width: { raw: 320, css: STANDARD_WIDTH },
      height: { raw: 224, css: pxToRem(224) },
    },
    small: {
      width: { raw: 260, css: AUDIO_WIDTH },
      height: { raw: 124, css: pxToRem(124) },
    },
    large: {
      width: { raw: 320, css: STANDARD_WIDTH },
      height: { raw: 320, css: pxToRem(320) },
    },
  },
  4: {
    default: {
      width: { raw: 221, css: STANDARD_WIDTH },
      height: { raw: 192, css: pxToRem(192) },
    },
    small: {
      width: { raw: 221, css: AUDIO_WIDTH },
      height: { raw: 124, css: pxToRem(124) },
    },
    large: {
      width: { raw: 221, css: STANDARD_WIDTH },
      height: { raw: 221, css: pxToRem(221) },
    },
  },
};

const DRAGGING_CLASSES = ["tw-cursor-grabbing", "[&_*]:!tw-pointer-events-none", "[&_*]:!tw-select-none"];

interface Node {
  id: string;
  type: "image" | "audio" | "video" | "composition" | "music";
  size: { width: { raw: number; css: string }; height: { raw: number; css: string } };
  x: Size;
  y: Size;
  input?: ("image" | "image_start" | "image_end" | "audio" | "music" | "video")[];
  output?: boolean;
  model?: string;
  prompt?: string;
  percentages: number[];
  animateKey?: string;
  asset?: StaticImageData | string;
  placeholderAsset?: StaticImageData;
  length?: string;
  seed?: number;
}

interface Edge {
  from: string;
  to: string;
  slot: 0 | 1;
}

function snapToNearest(value: number, set: number[]) {
  return set.reduce((nearest, current) => (Math.abs(current - value) < Math.abs(nearest - value) ? current : nearest));
}

const colors = {
  image: { base: "0.63 0.21 249.86", highlight: "oklch(0.44 0.14 248.74)" },
  image_start: { base: "0.63 0.21 249.86", highlight: "oklch(0.44 0.14 248.74)" },
  image_end: { base: "0.63 0.21 249.86", highlight: "oklch(0.44 0.14 248.74)" },
  video: { base: "0.58 0.27 289.97", highlight: "rgb(99 45 216)" },
  composition: { base: "0.58 0.27 289.97", highlight: "rgb(99 45 216)" },
  audio: { base: "0.63 0.21 323.07", highlight: "rgb(114 2 129)" },
  music: { base: "0.63 0.21 323.07", highlight: "rgb(114 2 129)" },
} as const;

function Marker({
  media,
  type,
  index,
}: {
  type: "input" | "output";
  media: "image" | "image_start" | "image_end" | "audio" | "video" | "composition" | "music";
  index: number;
}) {
  return (
    <div
      className={clsx(
        "tw-absolute tw-rounded-full tw-size-6 tw-flex tw-items-center tw-justify-center",
        type === "input" ? "tw-bottom-3.5 tw-right-full tw-mr-1.5" : "tw-top-3.5 tw-left-full tw-ml-1.5",
      )}
      style={{
        background: `color-mix(in oklch, oklch(${colors[media].base}), rgb(255 255 255 / .7) 65%)`,
        color: colors[media].highlight,
        bottom: type === "input" ? pxToRem(14 + index * (24 + 12)) : undefined,
        top: type === "output" ? pxToRem(14 + index * (24 + 12)) : undefined,
      }}
    >
      {media === "image" && (
        <svg fill='none' viewBox='0 0 20 20' className='tw-size-3.5' aria-hidden>
          <path
            stroke='currentColor'
            strokeWidth='1.5'
            d='m13.333 16.667-5.488-5.489a1.667 1.667 0 0 0-2.357 0l-2.155 2.155M5 16.667h10c.92 0 1.667-.747 1.667-1.667V5c0-.92-.747-1.667-1.667-1.667H5c-.92 0-1.667.747-1.667 1.667v10c0 .92.747 1.667 1.667 1.667Zm8.75-8.75a1.667 1.667 0 1 1-3.333 0 1.667 1.667 0 0 1 3.333 0Z'
          />
        </svg>
      )}
      {media === "image_start" && (
        <svg viewBox='0 0 14 14' fill='none' className='tw-size-3.5' aria-hidden>
          <path
            d='M7.583 4.083h3.5c.645 0 1.167.523 1.167 1.167v3.5c0 .644-.522 1.167-1.167 1.167h-3.5M2.917 4.083c-.645 0-1.167.523-1.167 1.167v3.5c0 .644.522 1.167 1.167 1.167M5.25 2.333v9.334'
            stroke='currentColor'
            strokeWidth='1.16667'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
      {media === "image_end" && (
        <svg viewBox='0 0 14 14' fill='none' className='tw-size-3.5 -tw-scale-100' aria-hidden>
          <path
            d='M7.583 4.083h3.5c.645 0 1.167.523 1.167 1.167v3.5c0 .644-.522 1.167-1.167 1.167h-3.5M2.917 4.083c-.645 0-1.167.523-1.167 1.167v3.5c0 .644.522 1.167 1.167 1.167M5.25 2.333v9.334'
            stroke='currentColor'
            strokeWidth='1.16667'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
      {media === "video" && (
        <svg viewBox='0 0 14 14' fill='none' className='tw-size-3.5' aria-hidden>
          <path
            d='M1.75 4.083c0-.644.522-1.166 1.167-1.166h4.666c.645 0 1.167.522 1.167 1.166v5.834c0 .644-.522 1.166-1.167 1.166H2.917A1.167 1.167 0 011.75 9.917V4.083z'
            stroke='currentColor'
            strokeWidth='1.3'
            strokeLinejoin='round'
          />
          <path
            d='M9.072 5.672l2.334-1.167a.583.583 0 01.844.522v3.946a.583.583 0 01-.844.522L9.072 8.328a.583.583 0 01-.322-.522V6.194c0-.221.125-.423.322-.522z'
            stroke='currentColor'
            strokeWidth='1.3'
            strokeLinejoin='round'
          />
        </svg>
      )}
      {media === "audio" && (
        <svg viewBox='0 0 26 26' fill='none' className='tw-size-3.5' aria-hidden>
          <path
            d='M8.667 4.333v17.334M4.333 10.833v4.334M13 8.667v8.666M17.333 6.5v13m4.334-8.667v4.334'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      )}
      {media === "music" && (
        <svg fill='none' viewBox='0 0 20 20' className='tw-size-3.5' aria-hidden>
          <path
            stroke='currentColor'
            strokeLinejoin='round'
            strokeWidth='1.5'
            d='M8.09 15.003C8.09 16.106 6.95 17 5.545 17S3 16.106 3 15.003s1.14-1.997 2.545-1.997 2.545.894 2.545 1.997Zm0 0V6.425c0-1.07.754-2.01 1.846-2.304l3.82-1.027C15.382 2.656 17 3.806 17 5.398v7.208m0 0c0 1.103-1.14 1.997-2.545 1.997s-2.545-.894-2.545-1.997 1.14-1.997 2.545-1.997S17 11.503 17 12.606Z'
          />
        </svg>
      )}
    </div>
  );
}

function createRandom(seed: number = 0) {
  return function random() {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };
}

function Node({
  prompt,
  m,
  type,
  animateKey,
  size,
  x,
  y,
  reveal,
  input = [],
  output,
  model,
  percentages,
  dragOffset,
  animated,
  delay,
  enterDelay,
  asset,
  placeholderAsset,
  length,
  seed,
}: {
  m: M;
  prompt?: string;
  type: "image" | "audio" | "video" | "composition" | "music";
  animateKey?: string;
  size: { width: { raw: number; css: string }; height: { raw: number; css: string } };
  x: Size;
  y: Size;
  reveal?: boolean;
  input?: ("image" | "image_start" | "image_end" | "audio" | "video" | "music")[];
  output?: boolean;
  model?: string;
  percentages: number[];
  dragOffset: { x: MotionValue<number>; y: MotionValue<number> };
  animated: boolean;
  delay: number;
  enterDelay: number;
  asset?: StaticImageData | string;
  placeholderAsset?: StaticImageData;
  length?: string;
  seed?: number;
}) {
  const t = useTranslations("ClientKeys.FlowsDemo");
  const progress = useMotionValue(0);
  const percentage = useTransform(progress, p => `${snapToNearest(p, percentages)}%`);
  const random = createRandom(seed);

  const progressText = t.raw("progress");
  const dragControls = useDragControls();

  const headings = {
    image: t("node_headings.image"),
    video: t("node_headings.video"),
    audio: t("node_headings.tts"),
    music: t("node_headings.music"),
    composition: t("node_headings.composition"),
  };

  const promptTags: { startIndex: number; endIndex: number }[] = [];
  if (prompt) {
    let sourceIndex = 0;
    let transformedPrompt = "";

    while (sourceIndex < prompt.length) {
      const tagStart = prompt.indexOf("{", sourceIndex);

      if (tagStart === -1) {
        transformedPrompt += prompt.slice(sourceIndex);
        break;
      }

      transformedPrompt += prompt.slice(sourceIndex, tagStart);

      const tagEnd = prompt.indexOf("}", tagStart + 1);
      if (tagEnd === -1) {
        transformedPrompt += prompt.slice(tagStart);
        break;
      }

      const tagValue = prompt.slice(tagStart + 1, tagEnd);
      const replacementText = `@${tagValue}`;
      const replacementStart = transformedPrompt.length;

      transformedPrompt += replacementText;
      promptTags.push({
        startIndex: replacementStart,
        endIndex: replacementStart + replacementText.length - 1,
      });

      sourceIndex = tagEnd + 1;
    }

    prompt = transformedPrompt;
  }
  function getPromptTagStatus(index: number) {
    for (let i = 0; i < promptTags.length; i++) {
      const v = promptTags[i];
      if (index >= v.startIndex && index <= v.endIndex) {
        return index - v.startIndex === 0 ? "first" : index - v.endIndex === 0 ? "last" : "middle";
      }
    }
    return "none";
  }

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove(...DRAGGING_CLASSES);
    };
  }, []);

  return (
    <m.div
      drag
      dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
      dragElastic={0.12}
      dragControls={dragControls}
      onDrag={(_, info) => {
        const distance = Math.sqrt(info.offset.x ** 2 + info.offset.y ** 2);
        if (distance > 350) {
          dragControls.stop();
        }
      }}
      onDragStart={() => {
        document.documentElement.classList.add(...DRAGGING_CLASSES);
      }}
      onDragEnd={() => {
        document.documentElement.classList.remove(...DRAGGING_CLASSES);
      }}
      transition={{ duration: 0 }}
      whileDrag={{ boxShadow: "0 0 0 1.5px #2B7FFF" }}
      className='tw-absolute tw-cursor-grab tw-rounded-2xl'
      style={{
        top: y.css,
        left: x.css,
        width: size.width.css,
        height: size.height.css,
        x: dragOffset?.x,
        y: dragOffset?.y,
      }}
      exit={{ opacity: 0, transition: { delay } }}
      {...(animated
        ? {}
        : { initial: { opacity: 0 }, animate: { opacity: 1, transition: { delay: enterDelay + delay } } })}
    >
      <div className='tw-absolute tw-inset-px tw-bg-cream/30 tw-rounded-2xl tw-border tw-border-dashed tw-border-black/5' />
      <m.div
        {...(animated
          ? reveal
            ? { initial: { opacity: 1 }, animate: { START: { opacity: 0 }, [`${animateKey}_REVEAL`]: { opacity: 1 } } }
            : {}
          : {})}
        className={clsx("tw-absolute tw-inset-0 tw-bg-white tw-flex tw-flex-col tw-rounded-2xl")}
        style={{ boxShadow: "0 0 0.943px 0 rgba(0, 0, 0, 0.40), 0 1.65px 3.3px 0 rgba(0, 0, 0, 0.04)" }}
      >
        <div className='tw-flex tw-gap-1 tw-absolute tw-bottom-full tw-inset-x-0 tw-text-[0.75rem]/4 tw-mb-2 tw-text-gray-500'>
          {type === "image" && (
            <svg viewBox='0 0 26 26' fill='currentColor' className='tw-size-4 tw-flex-none' aria-hidden>
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M3.25 6.5A3.25 3.25 0 016.5 3.25h13a3.25 3.25 0 013.25 3.25v13a3.25 3.25 0 01-3.25 3.25h-13a3.25 3.25 0 01-3.25-3.25v-13zM6.5 5.417c-.598 0-1.083.485-1.083 1.083v8.218l.952-.952a3.25 3.25 0 014.596 0l6.817 6.817H19.5c.598 0 1.083-.485 1.083-1.083v-13c0-.598-.485-1.083-1.083-1.083h-13zm8.218 15.166l-5.285-5.285a1.083 1.083 0 00-1.532 0l-2.484 2.484V19.5c0 .598.485 1.083 1.083 1.083h8.218z'
              />
              <path d='M14.706 9.047l.518-1.036c.2-.4.77-.4.969 0l.518 1.036c.052.105.137.19.242.242l1.037.518c.399.2.399.77 0 .97l-1.037.518a.54.54 0 00-.242.242l-.518 1.036c-.2.4-.77.4-.97 0l-.517-1.036a.54.54 0 00-.242-.243l-1.037-.518c-.399-.2-.399-.769 0-.969l1.037-.518a.54.54 0 00.242-.242z' />
            </svg>
          )}
          {type === "video" && (
            <svg viewBox='0 0 26 26' fill='none' className='tw-size-4 tw-flex-none' aria-hidden>
              <path
                d='M5.417 5.417h9.75c1.196 0 2.166.97 2.166 2.166V9.75l3.85-1.924c.72-.36 1.567.163 1.567.969v8.41c0 .806-.848 1.33-1.568.97l-3.849-1.925v2.167c0 1.196-.97 2.166-2.166 2.166h-9.75a2.167 2.167 0 01-2.167-2.166V7.583c0-1.196.97-2.166 2.167-2.166z'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinejoin='round'
              />
              <path
                d='M13 13a2.708 2.708 0 11-5.417 0A2.708 2.708 0 0113 13z'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinejoin='round'
              />
            </svg>
          )}
          {type === "composition" && (
            <svg fill='none' viewBox='0 0 16 16' className='tw-size-4 tw-flex-none' aria-hidden>
              <path
                stroke='currentColor'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.333'
                d='M12.667 3.333H3.333C2.597 3.333 2 3.93 2 4.667v4C2 9.403 2.597 10 3.333 10h9.334C13.403 10 14 9.403 14 8.667v-4c0-.737-.597-1.334-1.333-1.334M2 12.667h12'
              />
              <path
                stroke='currentColor'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.333'
                d='m2 8.333 2.17-1.687a1.33 1.33 0 0 1 1.651.011L10 10M11 6.333h.007m.326 0a.333.333 0 1 1-.666 0 .333.333 0 0 1 .666 0M4.667 13.333a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333'
              />
            </svg>
          )}
          {type === "audio" && (
            <svg viewBox='0 0 26 26' fill='none' className='tw-size-4 tw-flex-none' aria-hidden>
              <path
                d='M8.667 4.333v17.334M4.333 10.833v4.334M13 8.667v8.666M17.333 6.5v13m4.334-8.667v4.334'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </svg>
          )}
          {type === "music" && (
            <svg fill='none' viewBox='0 0 20 20' className='tw-size-4 tw-flex-none' aria-hidden>
              <path
                stroke='currentColor'
                strokeLinejoin='round'
                strokeWidth='1.5'
                d='M8.09 15.003C8.09 16.106 6.95 17 5.545 17S3 16.106 3 15.003s1.14-1.997 2.545-1.997 2.545.894 2.545 1.997Zm0 0V6.425c0-1.07.754-2.01 1.846-2.304l3.82-1.027C15.382 2.656 17 3.806 17 5.398v7.208m0 0c0 1.103-1.14 1.997-2.545 1.997s-2.545-.894-2.545-1.997 1.14-1.997 2.545-1.997S17 11.503 17 12.606Z'
              />
            </svg>
          )}
          <div className={clsx(model && "lg:tw-hidden xl:tw-block")}>{headings[type]}</div>
          {model && <div className='tw-ml-auto lg:tw-ml-0 xl:tw-ml-auto'>{model}</div>}
        </div>

        {output && <Marker media={type} type='output' index={0} />}

        {input.map((input, index) => (
          <Marker key={index} media={input} type='input' index={index} />
        ))}

        <div
          className={clsx(
            "tw-bg-cream tw-relative tw-overflow-hidden",
            prompt ? "tw-rounded-t-2xl" : "tw-rounded-2xl",
            type === "audio" || type === "music" ? "tw-h-12" : "tw-flex-auto",
          )}
        >
          <div className='tw-absolute tw-inset-0 tw-flex tw-flex-col tw-gap-2 tw-items-center tw-justify-center tw-text-gray-400'>
            {type === "image" && (
              <svg fill='none' viewBox='0 0 20 20' className='tw-size-6' aria-hidden>
                <path
                  stroke='currentColor'
                  strokeWidth='1.5'
                  d='m13.333 16.667-5.488-5.489a1.667 1.667 0 0 0-2.357 0l-2.155 2.155M5 16.667h10c.92 0 1.667-.747 1.667-1.667V5c0-.92-.747-1.667-1.667-1.667H5c-.92 0-1.667.747-1.667 1.667v10c0 .92.747 1.667 1.667 1.667Zm8.75-8.75a1.667 1.667 0 1 1-3.333 0 1.667 1.667 0 0 1 3.333 0Z'
                />
              </svg>
            )}
            {(type === "video" || type === "composition") && (
              <svg viewBox='0 0 14 14' fill='none' className='tw-size-6' aria-hidden>
                <path
                  d='M1.75 4.083c0-.644.522-1.166 1.167-1.166h4.666c.645 0 1.167.522 1.167 1.166v5.834c0 .644-.522 1.166-1.167 1.166H2.917A1.167 1.167 0 011.75 9.917V4.083z'
                  stroke='currentColor'
                  strokeWidth='1.3'
                  strokeLinejoin='round'
                />
                <path
                  d='M9.072 5.672l2.334-1.167a.583.583 0 01.844.522v3.946a.583.583 0 01-.844.522L9.072 8.328a.583.583 0 01-.322-.522V6.194c0-.221.125-.423.322-.522z'
                  stroke='currentColor'
                  strokeWidth='1.3'
                  strokeLinejoin='round'
                />
              </svg>
            )}
            {type === "audio" && (
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='tw-size-5'
                aria-hidden
              >
                <path d='M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z' />
                <path d='M16 9a5 5 0 0 1 0 6' />
                <path d='M19.364 18.364a9 9 0 0 0 0-12.728' />
              </svg>
            )}
            {type === "music" && (
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='tw-size-5'
                aria-hidden
              >
                <path d='M9 18V5l12-2v13' />
                <circle cx='6' cy='18' r='3' />
                <circle cx='18' cy='16' r='3' />
              </svg>
            )}
          </div>

          <m.div
            className='tw-absolute tw-inset-0 tw-bg-cream'
            onAnimationStart={() => {
              animate(progress, 100, { duration: 1, delay: 0.25 });
            }}
            initial={{ opacity: 0, transform: "translateX(0%)" }}
            animate={
              animateKey
                ? {
                    [`${animateKey}_LOADING`]: {
                      opacity: 1,
                      transform: "translateX(2.5%)",
                      transition: { transform: { duration: 1.25 } },
                    },
                  }
                : {}
            }
          >
            <m.div
              className='tw-absolute tw-inset-y-0 tw-right-0 tw-w-[200%]'
              style={{
                backgroundImage:
                  "repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.34), rgba(255, 255, 255, 0.34) 8px, rgba(255, 255, 255, 0) 8px, rgba(255, 255, 255, 0) 16px)",
                maskImage: "linear-gradient(to right, black 50%, transparent 100%)",
              }}
              initial={{ transform: "translateX(0%)" }}
              animate={
                animateKey
                  ? {
                      [`${animateKey}_LOADING`]: {
                        transform: "translateX(2.5%)",
                        transition: { duration: 1.25, ease: "linear" },
                      },
                    }
                  : {}
              }
            />
          </m.div>

          <m.div
            initial={{ opacity: 0 }}
            animate={animateKey ? { [`${animateKey}_LOADING`]: { opacity: 1 } } : {}}
            className='tw-absolute tw-top-2 tw-left-2 tw-bg-white tw-rounded tw-px-1.5 tw-text-[0.6875rem]/5 tw-tabular-nums tw-font-medium'
          >
            {typeof progressText === "string"
              ? progressText
                  .split(/(\{percentage\})/)
                  .map((part, partIndex) =>
                    part === "{percentage}" ? <m.span key={partIndex}>{percentage}</m.span> : part,
                  )
              : null}
          </m.div>

          <m.div
            className='tw-absolute tw-inset-0'
            initial={{ opacity: 1 }}
            animate={
              animateKey
                ? {
                    [`${animateKey}_DONE`]: { opacity: [0, 1] },
                    ...(reveal
                      ? { [`${animateKey}_REVEAL`]: { opacity: 0, transition: { duration: 0 } } }
                      : { START: { opacity: 0 } }),
                  }
                : {}
            }
          >
            {type === "image" && asset && (
              <Image
                src={asset}
                alt=''
                fill
                className='tw-object-cover'
                placeholder='blur'
                sizes={typeof asset === "string" ? undefined : pxToRem((asset.width / asset.height) * 260)}
                priority
                draggable={false}
              />
            )}
            {(type === "video" || type === "composition") && typeof asset === "string" && (
              <>
                {placeholderAsset && (
                  <Image
                    src={placeholderAsset}
                    alt=''
                    fill
                    className='tw-object-cover'
                    placeholder='blur'
                    sizes={pxToRem((placeholderAsset.width / placeholderAsset.height) * 260)}
                    priority
                    draggable={false}
                  />
                )}
                <m.video
                  src={asset}
                  className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
                  style={animated && animateKey ? { IDLE: { opacity: 0 }, [`${animateKey}_DONE`]: { opacity: 1 } } : {}}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </>
            )}
            {type === "composition" && (
              <div className='tw-absolute tw-bg-white tw-rounded-md tw-p-1 tw-top-2 tw-right-2'>
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='tw-size-3.5 tw-text-gray-500'
                  aria-hidden
                >
                  <path d='M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z' />
                  <path d='M16 9a5 5 0 0 1 0 6' />
                  <path d='M19.364 18.364a9 9 0 0 0 0-12.728' />
                </svg>
              </div>
            )}
            {(type === "audio" || type === "music") && (
              <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-bg-cream tw-p-3 tw-gap-3'>
                <div className='tw-rounded-full tw-bg-black/10 tw-size-6 tw-flex-none tw-flex tw-items-center tw-justify-center'>
                  <svg viewBox='0 0 20 20' fill='currentColor' className='tw-size-3.5' aria-hidden>
                    <path d='M8.38399 3.68914C7.05155 2.86616 5.33301 3.82463 5.33301 5.39074V14.6094C5.33301 16.1755 7.05155 17.134 8.38399 16.311L15.8467 11.7016C17.1121 10.9201 17.1121 9.07998 15.8467 8.29846L8.38399 3.68914Z' />
                  </svg>
                </div>
                {length && <div className='tw-text-[0.875rem]/5 tw-text-black'>{length}</div>}
                <div className='tw-flex-auto tw-min-w-0'>
                  <div
                    className='tw-overflow-hidden tw-flex tw-items-center tw-gap-0.5'
                    style={{ width: "round(down, 100%, 0.3125rem)" }}
                  >
                    {Array.from({ length: 32 }).map((_, i) => {
                      const height = Math.round(4 + random() * 10);
                      return (
                        <m.div
                          key={i}
                          className='tw-bg-black/15 tw-rounded-full tw-w-0.75 tw-flex-none'
                          style={{ height: pxToRem(height) }}
                          initial={{ transform: "scaleY(1)" }}
                          animate={{
                            [`${animateKey}_DONE`]: {
                              transform: [`scaleY(${3 / height})`, "scaleY(1)"],
                              transition: { delay: i * 0.01 },
                            },
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </m.div>
        </div>
        {prompt && (
          <div className='tw-type-2xs tw-text-black tw-p-3'>
            <div className='tw-text-pretty'>
              {animateKey
                ? prompt.split("").map((char, charIndex) => {
                    const tagStatus = getPromptTagStatus(charIndex);
                    return (
                      <m.span
                        key={charIndex}
                        initial={{ opacity: 1 }}
                        animate={{
                          ...(reveal
                            ? { [`${animateKey}_REVEAL`]: { opacity: 0, transition: { duration: 0 } } }
                            : { START: { opacity: 0 } }),
                          [`${animateKey}_PROMPT`]: {
                            opacity: 1,
                            transition: { duration: 0, delay: charIndex * 0.02 },
                          },
                        }}
                        style={
                          tagStatus !== "none"
                            ? {
                                backgroundColor: "rgb(240 246 255)",
                                color: "rgb(29 79 215)",
                                paddingTop: pxToRem(0.5),
                                paddingBottom: pxToRem(0.5),
                                paddingLeft: tagStatus === "first" ? pxToRem(2) : 0,
                                borderTopLeftRadius: tagStatus === "first" ? pxToRem(4) : 0,
                                borderBottomLeftRadius: tagStatus === "first" ? pxToRem(4) : 0,
                                paddingRight: tagStatus === "last" ? pxToRem(2) : 0,
                                borderTopRightRadius: tagStatus === "last" ? pxToRem(4) : 0,
                                borderBottomRightRadius: tagStatus === "last" ? pxToRem(4) : 0,
                              }
                            : {}
                        }
                      >
                        {char}
                      </m.span>
                    );
                  })
                : prompt}
            </div>
          </div>
        )}
      </m.div>
    </m.div>
  );
}

function Edge({
  m,
  type = "image",
  from,
  to,
  animateKey,
  fromOffset,
  toOffset,
  animated,
  delay,
  enterDelay,
}: {
  m: M;
  type?: "image" | "audio" | "video" | "composition" | "music";
  from: { x: Size; y: Size };
  to: { x: Size; y: Size };
  animateKey?: string;
  fromOffset: { x: MotionValue<number>; y: MotionValue<number> };
  toOffset: { x: MotionValue<number>; y: MotionValue<number> };
  animated: boolean;
  delay: number;
  enterDelay: number;
}) {
  const progress = useMotionValue(1);

  const w = to.x.raw - from.x.raw;
  const h = to.y.raw - from.y.raw;

  const path = useTransform(
    [progress, fromOffset.x, fromOffset.y, toOffset.x, toOffset.y],
    ([t, fdx, fdy, tdx, tdy]: number[]) => {
      const effW = w + tdx - fdx;
      const effH = h + tdy - fdy;
      const absW = Math.abs(effW) || 1;
      const absH = Math.abs(effH) || 1;

      const sx = effW >= 0 ? 0 : absW;
      const sy = effH >= 0 ? 0 : absH;
      const tx = effW >= 0 ? absW : 0;
      const ty = effH >= 0 ? absH : 0;

      const ex = sx + (tx - sx) * t;
      const ey = sy + (ty - sy) * t;
      const mx = (sx + ex) / 2;

      return `M ${sx} ${sy} C ${mx} ${sy}, ${mx} ${ey}, ${ex} ${ey}`;
    },
  );

  const dynamicViewBox = useTransform(
    [fromOffset.x, fromOffset.y, toOffset.x, toOffset.y],
    ([fdx, fdy, tdx, tdy]: number[]) => `0 0 ${Math.abs(w + tdx - fdx) || 1} ${Math.abs(h + tdy - fdy) || 1}`,
  );

  const stroke =
    type === "image"
      ? "oklch(0.63 0.21 249.86)"
      : type === "video"
        ? "oklch(0.58 0.27 289.97)"
        : "oklch(0.63 0.21 323.07)";

  return (
    <m.svg
      fill='none'
      className='tw-absolute tw-overflow-visible'
      style={{
        top: useTransform(
          [fromOffset.y, toOffset.y],
          ([fy, ty]: number[]) => `min(calc(${from.y.css} + ${fy}px), calc(${to.y.css} + ${ty}px))`,
        ),
        left: useTransform(
          [fromOffset.x, toOffset.x],
          ([fx, tx]: number[]) => `min(calc(${from.x.css} + ${fx}px), calc(${to.x.css} + ${tx}px))`,
        ),
        width: useTransform([fromOffset.x, toOffset.x], ([fdx, tdx]: number[]) => {
          const a = `calc((${to.x.css}) + ${tdx}px - (${from.x.css}) - ${fdx}px)`;
          const b = `calc((${from.x.css}) + ${fdx}px - (${to.x.css}) - ${tdx}px)`;
          return `max(${a}, ${b})`;
        }),
        height: useTransform([fromOffset.y, toOffset.y], ([fdy, tdy]: number[]) => {
          const a = `calc((${to.y.css}) + ${tdy}px - (${from.y.css}) - ${fdy}px)`;
          const b = `calc((${from.y.css}) + ${fdy}px - (${to.y.css}) - ${tdy}px)`;
          return `max(${a}, ${b})`;
        }),
      }}
      viewBox={dynamicViewBox}
      preserveAspectRatio='none'
      exit={{ opacity: 0, transition: { delay } }}
      {...(animated
        ? {}
        : { initial: { opacity: 0 }, animate: { opacity: 1, transition: { delay: enterDelay + delay } } })}
    >
      <m.path
        d={path}
        stroke={stroke}
        strokeOpacity={0.35}
        strokeWidth={2}
        vectorEffect='non-scaling-stroke'
        {...(animated
          ? animateKey
            ? {
                initial: { opacity: 1 },
                animate: {
                  START: { opacity: 0 },
                  [animateKey]: { opacity: 1, pathLength: [0, 1], transition: { opacity: { duration: 0 } } },
                },
              }
            : {}
          : {})}
      />
    </m.svg>
  );
}

type M = {
  [Element in keyof typeof motion]: (
    props: Omit<React.ComponentPropsWithRef<typeof Motion<string, Element>>, "as" | "store">,
  ) => React.JSX.Element;
};

function FlowsDemo({
  shift,
  animated = true,
  delay = 0,
  nodes,
  edges,
  columns = 3,
  states,
}: {
  shift: string[];
  animated?: boolean;
  delay?: number;
  nodes: Node[];
  edges: Edge[];
  columns: 3 | 4;
  states: string[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const { motion: m, start } = useAnimationSequence(states);

  useEffect(() => {
    if (!ref.current || !animated) {
      return;
    }

    let inView = false;
    let delayElapsed = false;

    function check() {
      if (!startedRef.current && inView && delayElapsed) {
        startedRef.current = true;
        start();
      }
    }

    const timer = window.setTimeout(() => {
      delayElapsed = true;
      check();
    }, 1500);

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        inView = true;
        check();
      }
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [start, animated]);

  const nodeDragOffsets: Record<string, { x: MotionValue<number>; y: MotionValue<number> }> = nodes.reduce(
    (acc, node) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      acc[node.id] = { x: useMotionValue(0), y: useMotionValue(0) };
      return acc;
    },
    {} as Record<string, { x: MotionValue<number>; y: MotionValue<number> }>,
  );

  return (
    <div
      ref={ref}
      className={clsx(
        "tw-absolute tw-inset-0 tw-overflow-hidden tw-py-16 -tw-my-16 tw-pointer-events-none",
        !animated && "tw-overflow-x-auto tw-scrollbar-none",
      )}
    >
      <div className='tw-relative tw-isolate tw-pointer-events-auto container'>
        {/* Overlay to prevent dragging nodes on smaller screens, so it doesn't interfere with scrolling */}
        <div className='tw-absolute -tw-top-6 tw-bottom-0 -tw-inset-x-10 tw-z-50 lg:tw-hidden' />

        <div className='xl:-tw-mx-10 tw-select-none tw-flex [container-type:inline-size]' role='img'>
          <m.div
            className={clsx(
              "tw-relative tw-h-[29.5rem] tw-mx-auto tw-flex-none lg:tw-flex-auto lg:tw-w-full",
              "[--standard-width:min(20rem,calc(100cqw-4rem))] lg:[--standard-width:calc((100%-(var(--gap)*(var(--columns)-1)))/var(--columns))]",
              columns == 3
                ? "tw-w-[calc((var(--standard-width)*3)+(var(--gap)*2))]"
                : "tw-w-[calc((var(--standard-width)*4)+(var(--gap)*3))]",
              columns === 3 ? "[--gap:8rem] xl:[--gap:9.25rem]" : "[--gap:8rem] xl:[--gap:7.75rem]",
              columns === 3
                ? [
                    "[--transform-0:translateX(min(0px,calc(-1*var(--standard-width)+(-1*var(--gap))+50cqw-var(--standard-width)/2)))]",
                    "sm:[--transform-0:translateX(min(0px,calc(-100%+100cqw+var(--standard-width)+var(--gap)-2rem-0.375rem)))]",
                    "[--transform-1:translateX(min(0px,calc(-100%+100cqw)))]",
                  ]
                : [
                    "[--transform-0:translateX(min(0px,calc(-1*var(--standard-width)+(-1*var(--gap))+50cqw-var(--standard-width)/2)))]",
                    "sm:[--transform-0:translateX(min(0px,calc(-100%+100cqw+(var(--standard-width)+var(--gap))*2-2rem-0.375rem)))]",

                    "[--transform-1:translateX(min(0px,calc((-1*var(--standard-width)+(-1*var(--gap)))*2+50cqw-var(--standard-width)/2)))]",
                    "sm:[--transform-1:translateX(min(0px,calc(-100%+100cqw+var(--standard-width)+var(--gap)-2rem-0.375rem)))]",

                    "[--transform-2:translateX(min(0px,calc(-100%+100cqw)))]",
                  ],
            )}
            initial={{ transform: "translateX(0%)" }}
            animate={Object.fromEntries(shift.map((s, i) => [s, { transform: `var(--transform-${i})` }]))}
            style={{ "--columns": columns } as React.CSSProperties}
          >
            {nodes.map((node, nodeIndex) => (
              <Node
                key={node.id}
                m={m}
                animateKey={node.animateKey}
                reveal={nodeIndex > 0}
                dragOffset={nodeDragOffsets[node.id]}
                animated={animated}
                delay={nodeIndex * 0.1}
                enterDelay={delay}
                {...node}
              />
            ))}
            {edges.map((edge, edgeIndex) => {
              const fromNode = nodes.find(node => node.id === edge.from);
              const toNode = nodes.find(node => node.id === edge.to);
              if (!fromNode || !toNode) return null;
              const fromWidth = fromNode.size.width;
              const toHeight = toNode.size.height;
              const markerSize = 24;
              const from = {
                x: {
                  raw: fromNode.x.raw + fromWidth.raw + 6 + markerSize,
                  css: `calc((${fromNode.x.css}) + (${fromWidth.css}) + 0.375rem + ${pxToRem(markerSize)})`,
                },
                y: {
                  raw: fromNode.y.raw + 14 + markerSize / 2,
                  css: `calc(${fromNode.y.css} + 0.875rem + ${pxToRem(markerSize)} / 2)`,
                },
              };
              const to = {
                x: {
                  raw: toNode.x.raw - 6 - markerSize,
                  css: `calc((${toNode.x.css}) - 0.375rem - ${pxToRem(markerSize)})`,
                },
                y: {
                  raw: toNode.y.raw + toHeight.raw - 14 - markerSize / 2 - (markerSize + 12) * edge.slot,
                  css: `calc(${toNode.y.css} + ${toHeight.css} - 0.875rem - ${pxToRem(markerSize / 2)} - (${pxToRem(markerSize + 12)} * ${edge.slot}))`,
                },
              };
              return (
                <Edge
                  key={edge.from + edge.to + edge.slot}
                  type={fromNode.type}
                  from={from}
                  to={to}
                  m={m}
                  animateKey={`EDGE_${edgeIndex + 1}`}
                  fromOffset={nodeDragOffsets[edge.from]}
                  toOffset={nodeDragOffsets[edge.to]}
                  delay={0.05 + edgeIndex * 0.1}
                  animated={animated}
                  enterDelay={delay}
                />
              );
            })}
          </m.div>
          <div className='tw-flex-none tw-w-[--outer-gutter] lg:tw-hidden' />
        </div>
      </div>
    </div>
  );
}

export function BlockFlowsDemo(props: FlowsDemoBlock) {
  const t = useTranslations("ClientKeys.FlowsDemo");

  const allDemos: Record<string, React.ComponentProps<typeof FlowsDemo>> = {
    yacht_promo: {
      columns: 4,
      shift: ["EDGE_1", "VIDEO_REVEAL", "COMPOSITION_REVEAL"],
      states: [
        "IDLE",
        "START",
        "IMAGE_PROMPT",
        "IMAGE_LOADING",
        "IMAGE_DONE",
        "EDGE_1",
        "IMAGE_2_REVEAL",
        "IMAGE_2_PROMPT",
        "IMAGE_2_LOADING",
        "IMAGE_2_DONE",
        "EDGE_2",
        "IMAGE_3_REVEAL",
        "IMAGE_3_PROMPT",
        "IMAGE_3_LOADING",
        "IMAGE_3_DONE",
        "VIDEO_REVEAL",
        "EDGE_3",
        "EDGE_4",
        "VIDEO_PROMPT",
        "VIDEO_LOADING",
        "VIDEO_DONE",
        "AUDIO_REVEAL",
        "AUDIO_PROMPT",
        "AUDIO_LOADING",
        "AUDIO_DONE",
        "COMPOSITION_REVEAL",
        "EDGE_5",
        "EDGE_6",
        "COMPOSITION_LOADING",
        "COMPOSITION_DONE",
      ],
      nodes: [
        {
          id: "image_reference",
          type: "image",
          prompt: t("scenario_yacht_promo.reference_prompt"),
          size: SIZES[4].large,
          x: { raw: 0, css: "0px" },
          y: { raw: 100, css: pxToRem(100) },
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE",
          asset: imgBoatReference,
        },
        {
          id: "image_top_down",
          type: "image",
          prompt: t("scenario_yacht_promo.top_down_prompt"),
          size: SIZES[4].default,
          x: {
            raw: SIZES[4].default.width.raw + GAP_4_COL,
            css: `calc((${STANDARD_WIDTH} + ${GAP}))`,
          },
          y: { raw: 0, css: pxToRem(0) },
          input: ["image"],
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE_2",
          asset: imgBoatTopDown,
        },
        {
          id: "image_close_up",
          type: "image",
          prompt: t("scenario_yacht_promo.close_up_prompt"),
          size: SIZES[4].default,
          x: {
            raw: SIZES[4].default.width.raw + GAP_4_COL,
            css: `calc((${STANDARD_WIDTH} + ${GAP}))`,
          },
          y: { raw: 280, css: pxToRem(280) },
          input: ["image"],
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE_3",
          asset: imgBoatCloseUp,
        },
        {
          id: "video",
          type: "video",
          size: SIZES[4].default,
          x: {
            raw: (SIZES[4].default.width.raw + GAP_4_COL) * 2,
            css: `calc((${STANDARD_WIDTH} + ${GAP}) * 2)`,
          },
          y: { raw: 16, css: pxToRem(16) },
          output: true,
          input: ["image_end", "image_start"],
          prompt: t("scenario_yacht_promo.video_prompt"),
          model: "Veo 3.1",
          percentages: [0, 18, 35, 51, 78, 95, 100],
          animateKey: "VIDEO",
          asset:
            "https://eleven-public-cdn.elevenlabs.io/payloadcms/tgesdikuoio-ElevenLabs_video_veo-3-1_camera%20moves..._2026-02-20T17_05_44.mp4",
          placeholderAsset: imgBoatTopDown,
        },
        {
          id: "voiceover",
          type: "audio",
          length: "00:04",
          seed: 928,
          size: SIZES[4].small,
          x: {
            raw: CONTAINER_WIDTH - SIZES[4].default.width.raw - GAP_4_COL - SIZES[4].small.width.raw,
            css: `max(calc((${STANDARD_WIDTH} + ${GAP}) * 2), calc(100% - (${STANDARD_WIDTH}) - ${GAP} - ${AUDIO_WIDTH}))`,
          },
          y: { raw: 300, css: pxToRem(300) },
          output: true,
          prompt: t("scenario_yacht_promo.audio_prompt"),
          model: "Eleven v3",
          percentages: [0, 7, 15, 30, 48, 70, 92, 100],
          animateKey: "AUDIO",
        },
        {
          id: "output",
          type: "composition",
          size: SIZES[4].large,
          x: {
            raw: CONTAINER_WIDTH - SIZES[4].default.width.raw,
            css: `max(calc((${STANDARD_WIDTH} + ${GAP}) * 3), calc(100% - ${STANDARD_WIDTH}))`,
          },
          y: { raw: 60, css: pxToRem(60) },
          input: ["audio", "video"],
          percentages: [0, 21, 42, 72, 85, 91, 100],
          animateKey: "COMPOSITION",
          asset:
            "https://eleven-public-cdn.elevenlabs.io/payloadcms/tgesdikuoio-ElevenLabs_video_veo-3-1_camera%20moves..._2026-02-20T17_05_44.mp4",
          placeholderAsset: imgBoatTopDown,
        },
      ],
      edges: [
        { from: "image_reference", to: "image_top_down", slot: 0 },
        { from: "image_reference", to: "image_close_up", slot: 0 },
        { from: "image_top_down", to: "video", slot: 1 },
        { from: "image_close_up", to: "video", slot: 0 },
        { from: "video", to: "output", slot: 1 },
        { from: "voiceover", to: "output", slot: 0 },
      ],
    },
    perfume_ad: {
      columns: 4,
      shift: ["EDGE_1", "VIDEO_REVEAL", "COMPOSITION_REVEAL"],
      states: [
        "IDLE",
        "START",
        "IMAGE_BOTTLE_PROMPT",
        "IMAGE_BOTTLE_LOADING",
        "IMAGE_BOTTLE_DONE",
        "IMAGE_MODEL_REVEAL",
        "IMAGE_MODEL_PROMPT",
        "IMAGE_MODEL_LOADING",
        "IMAGE_MODEL_DONE",
        "EDGE_1",
        "EDGE_2",
        "IMAGE_COMP_REVEAL",
        "IMAGE_COMP_PROMPT",
        "IMAGE_COMP_LOADING",
        "IMAGE_COMP_DONE",
        "IMAGE_BALCONY_REVEAL",
        "IMAGE_BALCONY_PROMPT",
        "IMAGE_BALCONY_LOADING",
        "IMAGE_BALCONY_DONE",
        "VIDEO_REVEAL",
        "EDGE_3",
        "EDGE_4",
        "VIDEO_PROMPT",
        "VIDEO_LOADING",
        "VIDEO_DONE",
        "AUDIO_REVEAL",
        "AUDIO_PROMPT",
        "AUDIO_LOADING",
        "AUDIO_DONE",
        "COMPOSITION_REVEAL",
        "EDGE_5",
        "EDGE_6",
        "COMPOSITION_LOADING",
        "COMPOSITION_DONE",
      ],
      nodes: [
        {
          id: "image_bottle",
          type: "image",
          prompt: t("scenario_perfume_ad.bottle_prompt"),
          size: SIZES[4].default,
          x: { raw: 20, css: pxToRem(20) },
          y: { raw: 12, css: pxToRem(12) },
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE_BOTTLE",
          asset: imgPerfumeBottle,
        },
        {
          id: "image_model",
          type: "image",
          prompt: t("scenario_perfume_ad.model_prompt"),
          size: SIZES[4].default,
          x: { raw: 0, css: pxToRem(0) },
          y: { raw: 280, css: pxToRem(280) },
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE_MODEL",
          asset: imgPerfumeModel,
        },
        {
          id: "image_comp",
          type: "image",
          prompt: t("scenario_perfume_ad.comp_prompt", { tag1: "{Image1}", tag2: "{Image2}" }),
          size: SIZES[4].default,
          x: {
            raw: SIZES[4].default.width.raw + GAP_4_COL,
            css: `calc((${STANDARD_WIDTH} + ${GAP}))`,
          },
          y: { raw: 0, css: pxToRem(0) },
          input: ["image"],
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE_COMP",
          asset: imgPerfumeComp,
        },
        {
          id: "image_balcony",
          type: "image",
          prompt: t("scenario_perfume_ad.balcony_prompt"),
          size: SIZES[4].default,
          x: {
            raw: SIZES[4].default.width.raw + GAP_4_COL + 16,
            css: `calc((${STANDARD_WIDTH} + ${GAP}) + ${pxToRem(16)})`,
          },
          y: { raw: 270, css: pxToRem(270) },
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE_BALCONY",
          asset: imgPerfumeBalcony,
        },
        {
          id: "video",
          type: "video",
          size: SIZES[4].default,
          x: {
            raw: (SIZES[4].default.width.raw + GAP_4_COL) * 2,
            css: `calc((${STANDARD_WIDTH} + ${GAP}) * 2)`,
          },
          y: { raw: 16, css: pxToRem(16) },
          output: true,
          input: ["image"],
          prompt: t("scenario_perfume_ad.video_prompt"),
          model: "Veo 3.1",
          percentages: [0, 18, 35, 51, 78, 95, 100],
          animateKey: "VIDEO",
          asset:
            "https://eleven-public-cdn.elevenlabs.io/payloadcms/48x71q70tfj-ElevenLabs_video_kling-o-3_Woman%20turns%20..._2026-03-10T06_23_24%20(2).mp4",
          placeholderAsset: imgPerfumeVideo,
        },
        {
          id: "music",
          type: "music",
          length: "00:30",
          seed: 259,
          size: SIZES[4].small,
          x: {
            raw: CONTAINER_WIDTH - SIZES[4].default.width.raw - GAP_4_COL - SIZES[4].small.width.raw,
            css: `max(calc((${STANDARD_WIDTH} + ${GAP}) * 2), calc(100% - (${STANDARD_WIDTH}) - ${GAP} - ${AUDIO_WIDTH}))`,
          },
          y: { raw: 300, css: pxToRem(300) },
          output: true,
          prompt: t("scenario_perfume_ad.music_prompt"),
          model: "Eleven Music",
          percentages: [0, 7, 15, 30, 48, 70, 92, 100],
          animateKey: "AUDIO",
        },
        {
          id: "output",
          type: "composition",
          size: SIZES[4].large,
          x: {
            raw: CONTAINER_WIDTH - SIZES[4].default.width.raw,
            css: `max(calc((${STANDARD_WIDTH} + ${GAP}) * 3), calc(100% - ${STANDARD_WIDTH}))`,
          },
          y: { raw: 60, css: pxToRem(60) },
          input: ["audio", "video"],
          percentages: [0, 21, 42, 72, 85, 91, 100],
          animateKey: "COMPOSITION",
          asset:
            "https://eleven-public-cdn.elevenlabs.io/payloadcms/48x71q70tfj-ElevenLabs_video_kling-o-3_Woman%20turns%20..._2026-03-10T06_23_24%20(2).mp4",
          placeholderAsset: imgPerfumeVideo,
        },
      ],
      edges: [
        { from: "image_bottle", to: "image_comp", slot: 0 },
        { from: "image_model", to: "image_comp", slot: 0 },
        { from: "image_comp", to: "video", slot: 0 },
        { from: "image_balcony", to: "video", slot: 0 },
        { from: "video", to: "output", slot: 1 },
        { from: "music", to: "output", slot: 0 },
      ],
    },
    running_track: {
      columns: 3,
      states: [
        "IDLE",
        "START",
        "IMAGE_PROMPT",
        "IMAGE_LOADING",
        "IMAGE_DONE",
        "EDGE_1",
        "VIDEO_REVEAL",
        "VIDEO_PROMPT",
        "VIDEO_LOADING",
        "VIDEO_DONE",
        "AUDIO_REVEAL",
        "AUDIO_PROMPT",
        "AUDIO_LOADING",
        "AUDIO_DONE",
        "COMPOSITION_REVEAL",
        "EDGE_2",
        "EDGE_3",
        "COMPOSITION_LOADING",
        "COMPOSITION_DONE",
      ],
      shift: ["EDGE_1", "COMPOSITION_REVEAL"],
      nodes: [
        {
          id: "start_frame",
          type: "image",
          prompt: t("scenario_running_track.image_prompt"),
          size: SIZES[3].large,
          x: { raw: 0, css: "0px" },
          y: { raw: 100, css: pxToRem(100) },
          output: true,
          model: "Nano Banana Pro",
          percentages: [0, 23, 40, 78, 90, 100],
          animateKey: "IMAGE",
          asset: imgRunner,
        },
        {
          id: "video",
          type: "video",
          size: SIZES[3].default,
          x: {
            raw: CONTAINER_WIDTH / 2 - SIZES[3].default.width.raw / 2,
            css: `max(calc(${STANDARD_WIDTH} + ${GAP}), calc(50% - ${STANDARD_WIDTH} / 2))`,
          },
          y: { raw: 0, css: pxToRem(0) },
          output: true,
          input: ["image_end", "image_start"],
          prompt: t("scenario_running_track.video_prompt"),
          model: "Veo 3.1",
          percentages: [0, 18, 35, 51, 78, 95, 100],
          animateKey: "VIDEO",
          asset: "https://eleven-public-cdn.elevenlabs.io/payloadcms/1k5k8jlm8kui-video.mp4",
          placeholderAsset: imgRunner,
        },
        {
          id: "voiceover",
          type: "audio",
          size: SIZES[3].small,
          length: "00:03",
          seed: 79,
          x: {
            raw: CONTAINER_WIDTH / 2 + SIZES[3].default.width.raw / 2 - SIZES[3].small.width.raw,
            css: `max(calc(${STANDARD_WIDTH} + ${GAP}), calc(50% + (${STANDARD_WIDTH} / 2) - ${AUDIO_WIDTH}))`,
          },
          y: { raw: 300, css: pxToRem(300) },
          output: true,
          prompt: t("scenario_running_track.audio_prompt"),
          model: "Eleven v3",
          percentages: [0, 7, 15, 30, 48, 70, 92, 100],
          animateKey: "AUDIO",
        },
        {
          id: "output",
          type: "composition",
          size: SIZES[3].large,
          x: {
            raw: CONTAINER_WIDTH - SIZES[3].default.width.raw,
            css: `max(calc((${STANDARD_WIDTH} + ${GAP}) * 2), calc(100% - ${STANDARD_WIDTH}))`,
          },
          y: { raw: 60, css: pxToRem(60) },
          input: ["audio", "video"],
          percentages: [0, 21, 42, 72, 85, 91, 100],
          animateKey: "COMPOSITION",
          asset: "https://eleven-public-cdn.elevenlabs.io/payloadcms/1k5k8jlm8kui-video.mp4",
          placeholderAsset: imgRunner,
        },
      ],
      edges: [
        { from: "start_frame", to: "video", slot: 1 },
        { from: "video", to: "output", slot: 1 },
        { from: "voiceover", to: "output", slot: 0 },
      ],
    },
  };

  const demos =
    props.demos
      ?.map(demo => {
        if (typeof demo.scenario === "string" && demo.scenario in allDemos) {
          return {
            ...allDemos[demo.scenario as keyof typeof allDemos],
            id: demo.scenario,
            title: demo.title,
          };
        }
        return null;
      })
      .filter(demo => demo !== null) ?? [];

  const [activeDemo, setActiveDemo] = useState<Key>(demos[0]?.id);
  const [key, setKey] = useState(0);
  const introAnimation = typeof props.introAnimation === "boolean" ? props.introAnimation : true;

  if (demos.length === 0) {
    return null;
  }

  if (demos.length === 1) {
    return (
      <div
        className='tw-flow-root tw-pt-20 tw-pb-14 breakout'
        style={{
          backgroundImage: "radial-gradient(circle at 1px 22px, rgb(0 0 0 / 0.15) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      >
        <div className='tw-relative tw-h-[29.5rem]'>
          <FlowsDemo {...demos[0]} animated={introAnimation} delay={0.3} />
        </div>
      </div>
    );
  }

  const link = getLinkProps(props.link);

  return (
    <Tabs
      className='tw-flex tw-flex-col-reverse tw-gap-10 tw-relative'
      selectedKey={activeDemo}
      onSelectionChange={id => {
        if (id === activeDemo) return;
        setActiveDemo(id);
        setKey(k => k + 1);
      }}
    >
      <div className='tw-z-50 tw-absolute tw-inset-x-0 tw-bottom-12'>
        <TabList
          size='md'
          aria-label={t("tabs_label")}
          centered
          background
          surface='paper'
          items={demos.map(demo => ({ id: demo.id, title: demo.title }))}
          trailingAction={
            link?.children && typeof link.href === "string"
              ? {
                  title: link.children,
                  href: link.href,
                  target: link.target,
                }
              : undefined
          }
        />
      </div>
      <div
        className='tw-flow-root tw-pt-20 tw-pb-32 breakout'
        style={{
          backgroundImage: "radial-gradient(circle at 1px 22px, rgb(0 0 0 / 0.15) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      >
        <div className='tw-relative tw-h-[29.5rem]'>
          <AnimatePresence mode='popLayout' initial={introAnimation}>
            {demos.map(demo =>
              demo.id === activeDemo ? (
                <TabPanel key={key} id={demo.id} shouldForceMount>
                  <FlowsDemo {...demo} animated={introAnimation && key === 0} delay={0.3} />
                </TabPanel>
              ) : null,
            )}
          </AnimatePresence>
        </div>
      </div>
    </Tabs>
  );
}
